import produce, {castDraft} from 'immer';
import reactUpdate from 'immutability-helper';
import isEqual from 'react-fast-compare';

import {assert} from 'Asserts';
import {Permissions} from 'auth/PermissionConstants';
import BenchlingConfig from 'BenchlingConfig';
import {DocumentUpdateSpec, EventConstants} from 'constants/EventConstants';
import * as context from 'Context';
import AppDispatcher, {FluxAction} from 'lib/Dispatcher';
import * as Metrics from 'metrics/Metrics';
import {
  Document,
  DocumentVersion,
  DocumentVersionUpdate,
  FromServerDocument,
  FromServerDocumentVersion,
  ToServerDocument,
  ToServerDocumentWithMetadata,
} from 'notebook/documents/DocumentConstants';
import DocumentsUpdater from 'notebook/documents/DocumentsUpdater';
import DocumentUpdater from 'notebook/documents/DocumentUpdater';
import DocumentUtil from 'notebook/documents/DocumentUtil';
import {CidIdMap, CidNotePartProvenanceIdMap} from 'notebook/notes/NoteConstants';
import {TableLimitUtils} from 'notebook/performance/tableLimitUtils';
import VersionedItemSubstore, {
  ItemEditorState,
  ItemSaveHandler,
  ItemVersionState,
} from 'notebook/store/VersionedItemSubstore';
import performStructuredTableMappings from 'notebook/structured-tables/structured-table-mappings/performStructuredTableMappings';
import {
  StructuredTableMappingsGlobalState,
  StructuredTableMappingsStateManager,
} from 'notebook/structured-tables/structured-table-mappings/StructuredTableMappingsState';
import {OutputTableMap} from 'notebook/tables/excess/Evaluator';
import {removeQueryResolutionsForTableFromSnapshotInNote} from 'notebook/tables/excess/Resolver';
import {
  getUpdatedNoteWithValidationErrors,
  getDocumentWithUpdatedValidationSnapshot,
} from 'notebook/tables/excess/ValidationErrorHandler';
import {createNoteRuntimeSnapshot, QueryResolution} from 'notebook/tables/TableStoreTransforms';
import {tableCellCountBucket} from 'notebook/util/analytics/TableAnalyticsUtils';
import {EntryNote, NotePart} from 'notebook/util/NoteUtilConstants';
import {getBucketStringForTag} from 'performance/PerformanceUtils';
import BaseEntryStore from 'stores/BaseEntryStore';
import {warn} from 'util/Logging';

/** A map from document id to the name to override it with */
type DocumentIdToNameMap = Record<string, string>;

// Store for a collection of Documents
class DocumentStore extends BaseEntryStore<
  Document,
  FromServerDocument,
  FromServerDocumentVersion,
  'document',
  'documentId'
> {
  documentNameOverrides: DocumentIdToNameMap = {};

  createItemSubstore(): VersionedItemSubstore<
    Document,
    NotePart,
    FromServerDocument,
    FromServerDocumentVersion,
    'document',
    'documentId'
  > {
    return new VersionedItemSubstore({
      options: {
        idKey: 'documentId',
        itemKey: 'document',
        getItemParts(item: Document) {
          return item.documentVersion.entryVersion.note.noteParts.map((itemPart) => ({
            itemPartId: itemPart.cid,
            itemPart,
          }));
        },
        getVersionCid(item: Document) {
          return item.documentVersion.cid;
        },
        asyncItemLoad: true,
        postSanitizeItem: this.postSanitizeItem.bind(this),
        sanitizeItem: DocumentUtil.sanitizeDocument.bind(this),
        invertPostProcess: DocumentUtil.removeTasksFromDocument.bind(this),
        prepareForStore: this.prepareForStore.bind(this),
        pickRevertableFields: DocumentUtil.pickRevertableFields.bind(this),
        doSave(id: string) {
          const DocumentActions = require('notebook/documents/DocumentActions').default;
          return DocumentActions.save(id);
        },
        enableItemPartLevelSaving: true,
      },
      events: {
        item: {
          load: {
            init: EventConstants.DOCUMENT_OPEN,
            completed: EventConstants.DOCUMENT_OPEN_COMPLETED,
            failed: EventConstants.DOCUMENT_OPEN_FAILED,
          },
          registerObjects: EventConstants.DOCUMENT_REGISTER_OBJECTS,
          close: EventConstants.DOCUMENT_CLOSE,
          dirty: EventConstants.DOCUMENT_INVALIDATE,
          rename: EventConstants.DOCUMENT_RENAME,
          resetToOpenVersion: EventConstants.DOCUMENT_RESET_TO_OPEN_VERSION,
          revert: EventConstants.DOCUMENT_REVERT,
          update: EventConstants.DOCUMENT_UPDATE,
          save: {
            init: EventConstants.DOCUMENT_SAVE,
            completed: EventConstants.DOCUMENT_SAVE_COMPLETED,
            failed: EventConstants.DOCUMENT_SAVE_FAILED,
          },
        },
        version: {
          load: {
            init: EventConstants.DOCUMENT_LOAD_VERSION,
            completed: EventConstants.DOCUMENT_LOAD_VERSION_COMPLETED,
            failed: EventConstants.DOCUMENT_LOAD_VERSION_FAILED,
          },
        },
        versions: {
          fetch: {
            init: EventConstants.DOCUMENT_FETCH_VERSIONS,
            completed: EventConstants.DOCUMENT_FETCH_VERSIONS_COMPLETED,
            failed: EventConstants.DOCUMENT_FETCH_VERSIONS_FAILED,
          },
        },
      },
    });
  }

  getNote(id: string): EntryNote {
    const document = this.getDocument(id);
    if (!document) {
      throw new Error(`Document ${id} not in store`);
    }
    return document.documentVersion.entryVersion.note;
  }

  override payloadHandler(action: FluxAction): boolean {
    // @ts-expect-error ts(2345)
    let shouldEmit = this.itemSubstore.handlePayload(action);
    // AFTER VersionedItemSubstore
    switch (action.actionType) {
      case EventConstants.DOCUMENT_OPEN: {
        const {documentId, relatedDocumentIdGetter} = action;
        if (relatedDocumentIdGetter) {
          this.itemSubstore.setRelatedDocumentIdGetter(documentId, relatedDocumentIdGetter);
        }
        break;
      }
      case EventConstants.DOCUMENT_OPEN_MANY: {
        const {documentIds, relatedDocumentIdGetter, ...restOfAction} = action;
        for (const documentId of documentIds) {
          this.itemSubstore.handlePayload({
            ...restOfAction,
            items: [],
            actionType: EventConstants.DOCUMENT_OPEN,
            documentId,
          });
          if (relatedDocumentIdGetter) {
            this.itemSubstore.setRelatedDocumentIdGetter(documentId, relatedDocumentIdGetter);
          }
        }
        shouldEmit = false;
        break;
      }
      case EventConstants.DOCUMENT_OPEN_COMPLETED_MANY: {
        const {documents, ...restOfAction} = action;
        for (const document of documents) {
          this.itemSubstore.handlePayload({
            ...restOfAction,
            items: [],
            actionType: EventConstants.DOCUMENT_OPEN_COMPLETED,
            documentId: document.id,
            document,
          });
        }
        shouldEmit = true;
        break;
      }
      case EventConstants.DOCUMENT_UPDATE_MANY: {
        const {documents, ...restOfAction} = action;
        for (const document of documents) {
          this.itemSubstore.handlePayload({
            ...restOfAction,
            items: [],
            actionType: EventConstants.DOCUMENT_UPDATE,
            documentId: document.id,
          });
        }
        shouldEmit = true;
        break;
      }
      case EventConstants.DOCUMENT_OPEN_FAILED_MANY: {
        const {documentIds, ...restOfAction} = action;
        for (const documentId of documentIds) {
          this.itemSubstore.handlePayload({
            ...restOfAction,
            items: [],
            actionType: EventConstants.DOCUMENT_OPEN_FAILED,
            documentId,
          });
        }
        shouldEmit = true;
        break;
      }
      case EventConstants.DOCUMENT_SAVE_MANY: {
        const {documentIds, ...restOfAction} = action;
        for (const documentId of documentIds) {
          this.itemSubstore.handlePayload({
            ...restOfAction,
            items: [],
            actionType: EventConstants.DOCUMENT_SAVE,
            documentId,
          });
        }
        shouldEmit = true;
        break;
      }
      case EventConstants.DOCUMENT_SAVE_COMPLETED_MANY: {
        const {payloads, updates, cidIdMap} = action;
        const itemsToProcess =
          payloads ??
          updates?.map(
            ({
              documentId,
              openVersion,
              documentVersionCid,
            }: {
              documentId: string;
              openVersion: number;
              documentVersionCid: string;
            }) => ({
              documentId,
              savedVersion: openVersion,
              versionId: cidIdMap[documentVersionCid],
              cidIdMap,
              cidNotePartProvenanceIdMap: undefined,
              lock: undefined,
            })
          ) ??
          [];

        for (const item of itemsToProcess) {
          const {
            documentId,
            savedVersion,
            versionId,
            cidIdMap: itemCidIdMap,
            cidNotePartProvenanceIdMap,
            lock,
          } = item;
          this.itemSubstore.handlePayload({
            items: [],
            actionType: EventConstants.DOCUMENT_SAVE_COMPLETED,
            documentId,
            savedVersion,
            versionId,
            cidIdMap: itemCidIdMap,
            serverData: {},
          });
          this.onDocumentSaveCompleted(documentId, cidNotePartProvenanceIdMap, lock);
        }
        shouldEmit = true;
        break;
      }
      case EventConstants.DOCUMENT_SAVE_COMPLETED: {
        const {documentId, cidNotePartProvenanceIdMap, lock} = action;
        shouldEmit = this.onDocumentSaveCompleted(documentId, cidNotePartProvenanceIdMap, lock);
        break;
      }

      case EventConstants.STRUCTURED_TABLE_INVOKE_MAPPINGS: {
        const {fileId, sourceTableProvenanceUuid, options} = action;
        const document = this.getDocument(fileId);
        if (!document) {
          break;
        }
        const note = this.getNote(fileId);
        const tableMappingResult = performStructuredTableMappings(
          note,
          fileId,
          sourceTableProvenanceUuid,
          options
        );
        const updatedDocument = produce(document, (draft) => {
          draft.documentVersion.entryVersion.note = castDraft(tableMappingResult.updatedNote);
        });
        this.itemSubstore.updateItem(fileId, updatedDocument, {
          bumpVersion: true,
          multidocTableOp:
            tableMappingResult.tableOp != null ? {[fileId]: tableMappingResult.tableOp} : undefined,
        });
        shouldEmit = true;
        break;
      }

      case EventConstants.EXCESS_DELETE_SNAPSHOTTED_RESOLUTIONS_FOR_TABLE: {
        const {itemId, provenanceUuid, tableOp, lookupConfigIds} = action;
        const currDocument = this.getDocument(itemId);
        if (currDocument == null) {
          break;
        }
        const note = currDocument.documentVersion.entryVersion.note;
        const updatedDocument: Document = {
          ...currDocument,
          documentVersion: {
            ...currDocument.documentVersion,
            entryVersion: {
              ...currDocument.documentVersion.entryVersion,
              note: removeQueryResolutionsForTableFromSnapshotInNote(note, provenanceUuid, lookupConfigIds),
            },
          },
        };

        this.itemSubstore.updateItem(itemId, updatedDocument, {
          bumpVersion: true,
          multidocTableOp: {[itemId]: tableOp},
        });
        shouldEmit = true;
        break;
      }
      case EventConstants.EXCESS_FORCE_EVALUATION: {
        // Re-run eval and update the store with new async eval results without bumping the version (unless bumpVersion is true)
        const {fileId, bumpVersion = false} = action;
        // If the document isn't in the store anymore we can skip evaluation.
        if (!this.itemSubstore.inStore(fileId)) {
          return true;
        }

        const {document} = this.itemSubstore.getItemStateUnsafe(fileId);

        const relatedDocumentsMap: Map<string, Document> | null =
          BenchlingConfig.ELN_ENABLE_CROSS_STEP_DATA_REFS
            ? this.getRelatedDocumentsForEvaluation(fileId)
            : null;

        let updatedDocuments: Array<Document> = [];
        context.using({forceExcessRevaluate: true}, this, () => {
          if (relatedDocumentsMap) {
            updatedDocuments = this.runMultiDocumentEvaluation(
              relatedDocumentsMap,
              document,
              null,
              false,
              bumpVersion ?? false
            );
          } else {
            const updater = new DocumentUpdater(
              fileId,
              () => this.itemSubstore.getItem(fileId),
              (documentVersion) => {
                const DocumentActions = require('notebook/documents/DocumentActions').default;
                DocumentActions.update(fileId, {$merge: {documentVersion}}, {bumpVersion: false});
              }
            );
            updatedDocuments = [updater.run(document, document)];
          }
        });

        if (bumpVersion) {
          for (const updatedDocument of updatedDocuments) {
            const oldDocument =
              updatedDocument.id === fileId ? document : relatedDocumentsMap?.get(updatedDocument.id);
            // If we don't have the old document, just skip doing anything with it.
            if (!oldDocument) {
              continue;
            }
            if (this.hasEvaluationSnapshotChanged(oldDocument, updatedDocument)) {
              this.itemSubstore.updateItem(updatedDocument.id, updatedDocument, {
                bumpVersion: true,
                performTableEvaluation: false,
              });
            }
          }
        }

        shouldEmit = true;
        break;
      }
      case EventConstants.ENTRY_SET_FOCUSED_TABLE: {
        const {fileId, focusedTableProvenanceUuid} = action;
        if (!this.itemSubstore.inStore(fileId)) {
          break;
        }
        this.itemSubstore.mergeItem(fileId, {
          focusedTableProvenanceUuid,
        });
        shouldEmit = true;
        break;
      }
      case EventConstants.DOCUMENT_SERVER_SIDE_UPDATE: {
        // performServerSideWriteAction will already have refetched the document with the updated contents
        // This event is emitted, intentionally without any data, to allow other UI components using the
        // useDocument hook's onChange handler to also update their states accordingly. This is because the
        // new document will now be available in the DocumentStore and be picked up by any views that are
        // reading data from it on the re-render.
        shouldEmit = true;
        break;
      }
      case EventConstants.STRUCTURED_TABLE_VALIDATION_ERRORS: {
        const {entryId, validationErrorResponse} = action;
        const document = this.getDocument(entryId);
        if (document == null) {
          break;
        }
        const updatedDocument = produce(document, (draft) => {
          // @ts-expect-error ts(2322)
          draft.documentVersion.entryVersion.note = getUpdatedNoteWithValidationErrors<EntryNote>(
            document.documentVersion.entryVersion.note,
            validationErrorResponse
          );
        });
        this.itemSubstore.updateItem(entryId, updatedDocument, {bumpVersion: true});
        shouldEmit = true;
        break;
      }
      case EventConstants.DOCUMENT_UPDATE_RELATED_DOCUMENTS: {
        const {documentUpdate, relatedDocumentUpdates, options} = action;
        const {id: documentId, spec: documentSpec} = documentUpdate;
        const document = this.getDocument(documentId);
        assert(document != null, `Cannot find document with id ${documentId}`);
        const updatedDocument = reactUpdate(document, documentSpec);
        const updatedRelatedDocuments =
          relatedDocumentUpdates?.map((relatedDocumentUpdate: DocumentUpdateSpec) => {
            const relatedDocument = this.getDocument(relatedDocumentUpdate.id);
            assert(
              relatedDocument != null,
              `Cannot find related document with id ${relatedDocumentUpdate.id}`
            );
            return reactUpdate(relatedDocument, relatedDocumentUpdate.spec);
          }) ?? [];
        for (const updatedRelatedDocument of updatedRelatedDocuments) {
          this.itemSubstore.updateItem(updatedRelatedDocument.id, updatedRelatedDocument, {
            bumpVersion: true,
            performTableEvaluation: false,
          });
        }

        this.itemSubstore.updateItem(documentId, updatedDocument, {
          bumpVersion: true,
          performTableEvaluation: true,
          ...options,
        });
        shouldEmit = true;
        break;
      }
      case EventConstants.DOCUMENT_REGISTER_NAME_OVERRIDE: {
        const {documentId, nameOverride} = action;
        this.documentNameOverrides[documentId] = nameOverride;
        break;
      }
      default:
        break;
    }

    if (shouldEmit) {
      let id: string | undefined;
      // eslint-disable-next-line benchlint/no-in-operator
      if ('documentId' in action) {
        id = action.documentId;
        // eslint-disable-next-line benchlint/no-in-operator
      } else if ('fileId' in action) {
        id = action.fileId;
      } else if (window.debug) {
        warn(
          `DocumentStore Action "${action.actionType}" expected to emit change was called without a documentId or fileId`
        );
      }
      this.emitChange({id, actionType: action.actionType});
    }
    return true;
  }

  postSanitizeItem(
    id: string,
    newDocument: Document,
    oldDocument: Document | null,
    {
      bumpVersion,
      skipValidationStalenessCheck = false,
      performTableEvaluation = true,
    }: {bumpVersion?: boolean; skipValidationStalenessCheck: boolean; performTableEvaluation: boolean}
  ): Document {
    if (!performTableEvaluation) {
      return newDocument;
    }
    saveTableMappingsToGlobalState(id, newDocument, oldDocument);

    // TODO(BNCH-30610): Remove this edge case once persist eval has been fully rolled out (when ELN_ENABLE_RUNTIME_SNAPSHOT_EVAL will always be true)
    if (!BenchlingConfig.ELN_ENABLE_RUNTIME_SNAPSHOT_EVAL) {
      // If Excess Version is null we can no-op since it signifies an empty NRS
      if (newDocument.documentVersion.entryVersion.note.noteRuntimeSnapshot.excessVersion == null) {
        return newDocument;
      }
      const emptySnapshot = createNoteRuntimeSnapshot();
      return {
        ...newDocument,
        documentVersion: {
          ...newDocument.documentVersion,
          entryVersion: {
            ...newDocument.documentVersion.entryVersion,
            note: {
              ...newDocument.documentVersion.entryVersion.note,
              noteRuntimeSnapshot: emptySnapshot,
            },
          },
        },
      };
    }

    let relatedDocumentsMap: Map<string, Document> | null = null;
    if (BenchlingConfig.ELN_ENABLE_CROSS_STEP_DATA_REFS) {
      relatedDocumentsMap = this.getRelatedDocumentsForEvaluation(id);
    }
    // If we don't have the required related documents, we fall back to the normal evaluation called by
    // DocumentActions.UPDATE
    if (relatedDocumentsMap) {
      const startTime = performance.now();
      const updatedDocuments = this.runMultiDocumentEvaluation(
        relatedDocumentsMap,
        newDocument,
        oldDocument,
        skipValidationStalenessCheck,
        bumpVersion ?? false
      );
      const endTime = performance.now();

      let totalCells = 0;
      updatedDocuments.forEach((doc) => {
        const {numCells} = TableLimitUtils.analyzeNotePerformance(doc.documentVersion.entryVersion.note);
        totalCells += numCells;
      });

      Metrics.distribution('worksheet.eval_time_ms', endTime - startTime, {
        step_count: getBucketStringForTag(updatedDocuments.length, [5, 10, 15, 20, 30, 40, 50]),
        cell_count: tableCellCountBucket(totalCells),
      });

      for (const updatedDoc of updatedDocuments) {
        if (updatedDoc.id === id) {
          continue;
        }
        const oldDoc = relatedDocumentsMap.get(updatedDoc.id)!;
        if (this.hasEvaluationSnapshotChanged(oldDoc, updatedDoc)) {
          const updatedDocWithValidationSnapshot = getDocumentWithUpdatedValidationSnapshot({
            oldDocument: oldDoc,
            newDocument: updatedDoc,
            getNote: ({documentVersion}) => documentVersion.entryVersion.note,
            skipValidationStalenessCheck,
          });

          this.itemSubstore.updateItem(updatedDoc.id, updatedDocWithValidationSnapshot, {
            bumpVersion: bumpVersion ?? true,
            performTableEvaluation: false,
            skipValidationStalenessCheck,
          });
        }
      }

      const updatedPrimaryDocument = updatedDocuments.find((doc) => doc.id === id) ?? newDocument;
      return getDocumentWithUpdatedValidationSnapshot({
        oldDocument: oldDocument ?? undefined,
        newDocument: updatedPrimaryDocument,
        getNote: ({documentVersion}) => documentVersion.entryVersion.note,
        skipValidationStalenessCheck,
      });
    } else {
      const updater = new DocumentUpdater(
        id,
        () => this.itemSubstore.getItem(id),
        (documentVersion) => {
          const DocumentActions = require('notebook/documents/DocumentActions').default;
          DocumentActions.update(
            id,
            {
              $merge: {
                documentVersion: getDocumentWithUpdatedValidationSnapshot({
                  oldDocument: oldDocument?.documentVersion,
                  newDocument: documentVersion,
                  getNote: ({entryVersion}) => entryVersion.note,
                  skipValidationStalenessCheck,
                }),
              },
            },
            {bumpVersion}
          );
        }
      );

      return getDocumentWithUpdatedValidationSnapshot({
        oldDocument: oldDocument ?? undefined,
        newDocument: updater.run(newDocument, oldDocument),
        getNote: ({documentVersion}) => documentVersion.entryVersion.note,
        skipValidationStalenessCheck,
      });
    }
  }

  getDocument(documentId: string): Document | null {
    return this.itemSubstore.getItemState(documentId)?.document ?? null;
  }

  getDocumentPermissions(documentId: string): Permissions | null {
    return this.itemSubstore.getItemStateUnsafe(documentId).document.permissions;
  }

  prepareForStore(document: FromServerDocument, cidIdMap: CidIdMap): Omit<Document, 'latestVersionId'> {
    let documentForStore = DocumentUtil.prepareForStore(document, cidIdMap);
    if (BenchlingConfig.ELN_ENABLE_CROSS_STEP_DATA_REFS) {
      // When loading a document, replace the name with the override name if it exists
      const nameOverride = this.documentNameOverrides[document.id];
      if (nameOverride) {
        documentForStore = produce(documentForStore, (draft) => {
          draft.name = nameOverride;
        });
      }
    }
    return documentForStore;
  }

  prepareForSave(id: string, document: Document): ToServerDocument {
    const {cidIdMap} = this.itemSubstore.getItemStateUnsafe(id);
    const unsavedNoteParts = this.itemSubstore.getUnsavedItemParts(id);
    return DocumentUtil.prepareForSave(document, cidIdMap, unsavedNoteParts);
  }

  getDocumentVersionUpdate(id: string): DocumentVersionUpdate {
    const {openVersion, document} = this.getEditorState(id);
    return {openVersion, documentVersionCid: document.documentVersion.cid};
  }

  override getEditorState(id: string): ItemEditorState<Document, NotePart, 'document', 'documentId'> {
    return this.itemSubstore.getEditorState(id);
  }

  override getVersionState(id: string): ItemVersionState<FromServerDocumentVersion> {
    return this.itemSubstore.getVersionState(id);
  }

  getToServerDocument(id: string): {toServerDocument: ToServerDocumentWithMetadata; openVersion: number} {
    const {document, openVersion, metadata} = this.getEditorState(id);
    const toServerDocument = {...this.prepareForSave(id, document), metadata};
    return {openVersion, toServerDocument};
  }

  disableAutoSaver(id: string): void {
    this.itemSubstore.disableAutoSaver(id);
  }

  getCustomSaveHandler(id: string): ItemSaveHandler | undefined {
    return this.itemSubstore.getCustomSaveHandler(id);
  }

  setCustomSaveHandler(id: string, customSaveHandler: ItemSaveHandler): void {
    this.itemSubstore.setCustomSaveHandler(id, customSaveHandler);
  }

  setRelatedDocumentIdGetter(id: string, getRelatedDocumentIds: () => Array<string>): void {
    this.itemSubstore.setRelatedDocumentIdGetter(id, getRelatedDocumentIds);
  }

  getRelatedDocumentIds(id: string): Array<string> {
    return this.itemSubstore.getRelatedDocumentIds(id);
  }

  /**
   * Handles post-save updates for a document, updating provenance IDs and lock state.
   * @returns true if the document was updated, false if the document was not found
   */
  private onDocumentSaveCompleted(
    documentId: string,
    cidNotePartProvenanceIdMap: CidNotePartProvenanceIdMap | undefined,
    lock: Document['lock']
  ): boolean {
    const currDocument = this.getDocument(documentId);
    if (currDocument == null) {
      return false;
    }
    const updatedDocument = produce(currDocument, (draft) => {
      draft.documentVersion.entryVersion.note.noteParts.forEach((notePart) => {
        if (
          cidNotePartProvenanceIdMap &&
          cidNotePartProvenanceIdMap[notePart.cid] != null &&
          notePart.provenanceId !== cidNotePartProvenanceIdMap[notePart.cid]
        ) {
          notePart.provenanceId = cidNotePartProvenanceIdMap[notePart.cid];
        }
      });
      draft.lock = lock;
    });
    this.itemSubstore.updateItem(documentId, updatedDocument, {bumpVersion: false});
    return true;
  }

  private getRelatedDocumentsForEvaluation(documentId: string): Map<string, Document> | null {
    const currentItemState = this.itemSubstore.getItemStateUnsafe(documentId);
    if (!currentItemState.getRelatedDocumentIds) {
      return null;
    }
    const relatedDocumentIds: Array<string> =
      currentItemState.getRelatedDocumentIds().filter((id) => id !== documentId) ?? [];

    const relatedDocuments = new Map<string, Document>();
    for (const id of relatedDocumentIds) {
      const document = this.itemSubstore.getItem(id);
      // If a document is not in the store (because it's not loaded yet) return null to indicate that we do
      // not have the necessary related documents
      if (document) {
        relatedDocuments.set(id, document);
      } else {
        return null;
      }
    }
    return relatedDocuments;
  }

  private runMultiDocumentEvaluation(
    relatedDocumentsMap: Map<string, Document>,
    newPrimaryDocument: Document,
    oldPrimaryDocument: Document | null,
    skipValidationStalenessCheck: boolean,
    bumpVersion: boolean
  ): Array<Document> {
    const relatedDocuments = relatedDocumentsMap ? Array.from(relatedDocumentsMap.values()) : [];
    const documentsUpdater = new DocumentsUpdater(
      newPrimaryDocument.id, // fileId
      () => {
        const primaryDoc = this.itemSubstore.getItem(newPrimaryDocument.id);
        if (!primaryDoc) {
          return null;
        }
        const root = [primaryDoc];
        for (const relatedDocument of relatedDocuments) {
          const doc = this.itemSubstore.getItem(relatedDocument.id);
          assert(doc != null, 'Expected all related documents to be loaded and unloaded together');
          root.push(doc);
        }
        return root;
      },
      (documentVersions) => {
        const DocumentActions = require('notebook/documents/DocumentActions').default;
        const updatedDocIds: Array<string> = [];
        for (const [documentId, newDocumentVersion] of documentVersions) {
          const oldDocumentVersion =
            documentId === newPrimaryDocument.id
              ? oldPrimaryDocument?.documentVersion
              : relatedDocumentsMap.get(documentId)?.documentVersion;

          const getQueryResolutions = (documentVersion: DocumentVersion): Array<QueryResolution> =>
            documentVersion.entryVersion.note.noteRuntimeSnapshot?.queryResolutions ?? [];

          const newQueryResolutions = getQueryResolutions(newDocumentVersion);
          const oldQueryResolutions = oldDocumentVersion ? getQueryResolutions(oldDocumentVersion) : [];

          if (!isEqual(oldQueryResolutions, newQueryResolutions)) {
            updatedDocIds.push(documentId);
          }
        }

        if (updatedDocIds.length > 0) {
          DocumentActions.updateDocumentGroup(
            {
              id: updatedDocIds[0],
              spec: {
                $merge: {
                  documentVersion: documentVersions.get(updatedDocIds[0]),
                },
              },
            },
            updatedDocIds.slice(1).map((docId) => ({
              id: docId,
              spec: {
                $merge: {
                  documentVersion: documentVersions.get(docId),
                },
              },
            })),
            {bumpVersion, performTableEvaluation: true}
          );
        }
      }
    );

    const newDocuments = [newPrimaryDocument, ...relatedDocuments];
    const oldDocuments = oldPrimaryDocument ? [oldPrimaryDocument, ...relatedDocuments] : relatedDocuments;
    const updatedDocuments = documentsUpdater.run(newDocuments, oldDocuments);
    return updatedDocuments;
  }

  hasEvaluationSnapshotChanged(oldDocument: Document, newDocument: Document): boolean {
    function getEvaluationSnapshot(document: Document): OutputTableMap | null {
      return document.documentVersion.entryVersion.note.noteRuntimeSnapshot.evaluationSnapshot;
    }
    return !isEqual(getEvaluationSnapshot(oldDocument), getEvaluationSnapshot(newDocument));
  }
}

/** Save Table Mappings to the global state if they changed. */
function saveTableMappingsToGlobalState(
  documentId: string,
  newDocument: Document,
  oldDocument: Document | null
): void {
  const newTableMappings = DocumentUtil.getTableMappings(newDocument);
  // we optimize such that we can do plain equality checks
  if ((oldDocument && DocumentUtil.getTableMappings(oldDocument)) !== newTableMappings) {
    StructuredTableMappingsGlobalState.updateState(
      documentId,
      StructuredTableMappingsStateManager.setStructuredTableMappings(newTableMappings)
    );
  }
}

const documentStore = new DocumentStore(AppDispatcher);
export default documentStore;
